import { isEqual } from 'lodash';

import type {
  RecipeIngredient,
  ActivityIngredient,
  HotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { getFormattedTime } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import {
  isAlternativeActivityIngredientWithStaticInfoResponse,
  isAvailableActivityIngredientWithStaticInfoResponse,
} from '@libs/search-service-smart-search';
import type {
  HotelIngredientResponseWithStaticInfo,
  ActivityIngredientResponseWithStaticInfo,
  OwnStayIngredientResponseWithStaticInfo,
  AvailableActivityIngredientResponseWithStaticInfo,
  IngredientId,
  ActivitySuggestionWithStaticInfo,
  ActivityStaticInfo,
  AlternativeActivityIngredientResponseWithStaticInfo,
  ActivityBookableOptionTicket,
} from '@libs/trip-planner-interfaces';
import { SemboDate, DateFormat } from '@libs/sembo-dates';

import type {
  ActivityProductCardContent,
  ProductInfo,
  ItineraryActivitySegment,
  GenericActivityBannerData,
  ItineraryUpsellSegment,
  SuggestedActivitiesBannerData,
  StaticSuggestedActivitiesBannerData,
  AvailableItineraryActivitySegment,
  AlternativeTicketSuggestionActivitySegment,
  AlternativeActivitySuggestionData,
} from '../../models';
import {
  ItineraryActivitySegmentStatus,
  ItinerarySegmentType,
  ProductCardType,
  UnavailabilityReason,
} from '../../models';
import { getChangeButtonText } from '../tripOverviewStoreLogic';

import { REQUSTED_IMAGE_SIZE } from './tripOverviewHotelRelatedSegmentsLogic';

const { t } = i18n.global;

export function getActivitySegments(
  stayIngredient:
    | HotelIngredientResponseWithStaticInfo
    | OwnStayIngredientResponseWithStaticInfo,
  getIngredient: (id: string) => RecipeIngredient,
  ingredientIndex: number,
  selectedDate?: Date
): ItineraryActivitySegment[] {
  return stayIngredient.subIngredientResponseWithStaticInfo.reduce(
    (
      acc: ItineraryActivitySegment[],
      activityIngredientResponse: ActivityIngredientResponseWithStaticInfo
    ) => {
      const activityIngredient = getIngredient(
        activityIngredientResponse.ingredientId
      ) as ActivityIngredient;

      if (
        isAvailableActivityIngredientWithStaticInfoResponse(
          activityIngredientResponse
        ) &&
        !isFeatureToggleActive('show-availability-errors')
      ) {
        acc.push(
          getTripOverviewAvailableActivitySegment(
            stayIngredient.ingredientId,
            activityIngredientResponse,
            ingredientIndex
          )
        );
      } else if (
        isAlternativeActivityIngredientWithStaticInfoResponse(
          activityIngredientResponse
        ) &&
        !isFeatureToggleActive('show-availability-errors')
      ) {
        acc.push(
          getTripOverviewAlternativeActivitySegment(
            stayIngredient.ingredientId,
            activityIngredientResponse,
            ingredientIndex
          )
        );
      } else {
        acc.push(
          getTripOverviewUnavailableActivitySegment(
            stayIngredient.ingredientId,
            activityIngredient.ingredientId,
            activityIngredientResponse.date,
            ingredientIndex,
            selectedDate
          )
        );
      }

      return acc;
    },
    []
  );
}

export function getTripOverviewAvailableActivitySegment(
  parentIngredientId: IngredientId,
  ingredientResponse: AvailableActivityIngredientResponseWithStaticInfo,
  ingredientIndex: number
): AvailableItineraryActivitySegment {
  return {
    segmentType: ItinerarySegmentType.Activity,
    statusType: ItineraryActivitySegmentStatus.Available,
    productCardData: {
      imgSrcs: ingredientResponse.staticInfo.images.map((image) =>
        getImageUrl(image, undefined, REQUSTED_IMAGE_SIZE, REQUSTED_IMAGE_SIZE)
      ),
      productCardContent: getActivityProductCardContent(ingredientResponse),
    },
    context: {
      hotelCode: ingredientResponse.staticInfo.hotelCode,
      accommodationType: ingredientResponse.staticInfo.typeOfHotel,
      parentIngredientId,
      ingredientId: ingredientResponse.ingredientId,
      ingredientIndex,
    },
    date: new SemboDate(ingredientResponse.date).format(DateFormat.DateMonth),
    isAvailable: true,
  };
}

export function getActivityProductCardContent(
  ingredientResponse:
    | AvailableActivityIngredientResponseWithStaticInfo
    | AlternativeActivityIngredientResponseWithStaticInfo
): ActivityProductCardContent {
  return {
    header: ingredientResponse.staticInfo.name,
    subheader: '',
    rating: {
      stars: ingredientResponse.staticInfo.semboSunRating,
    },
    cardDetails: getActivityProductCardDetails(ingredientResponse),
    changeButtonText: getChangeButtonText(ProductCardType.Activity),
  };
}

export function getActivityProductCardDetails(
  activityIngredientResponse:
    | AvailableActivityIngredientResponseWithStaticInfo
    | AlternativeActivityIngredientResponseWithStaticInfo
): ProductInfo[] {
  const responseTickets = isAvailableActivityIngredientWithStaticInfoResponse(
    activityIngredientResponse
  )
    ? activityIngredientResponse.suggested.tickets
    : activityIngredientResponse.alternativeSuggestion.tickets;

  return responseTickets.map((alternativeTicket) => ({
    label: `${alternativeTicket.quantity} ${t(
      'planner.tickets',
      alternativeTicket.quantity
    )}`,
    description: getActivityProductCardDetailsDescription(alternativeTicket),
  }));
}

export function getActivityProductCardDetailsDescription(
  ticket: ActivityBookableOptionTicket
): string {
  const descriptions: string[] = [];

  descriptions.push(ticket.title);

  descriptions.push(ticket.description);

  if (ticket.timeSlotStartTime) {
    descriptions.push(getFormattedTime(ticket.timeSlotStartTime));
  }

  return descriptions.join(', ');
}

export function getTripOverviewAlternativeActivitySegment(
  parentIngredientId: IngredientId,
  ingredientResponse: AlternativeActivityIngredientResponseWithStaticInfo,
  ingredientIndex: number
): AlternativeTicketSuggestionActivitySegment {
  return {
    segmentType: ItinerarySegmentType.Activity,
    statusType: ItineraryActivitySegmentStatus.AlternativeTicket,
    productCardData: {
      imgSrcs: ingredientResponse.staticInfo.images.map((image) =>
        getImageUrl(image, undefined, REQUSTED_IMAGE_SIZE, REQUSTED_IMAGE_SIZE)
      ),
      productCardContent: getActivityProductCardContent(ingredientResponse),
    },
    context: {
      hotelCode: ingredientResponse.staticInfo.hotelCode,
      accommodationType: ingredientResponse.staticInfo.typeOfHotel,
      parentIngredientId,
      ingredientId: ingredientResponse.ingredientId,
      ingredientIndex,
    },
    alternativeSuggestionData: getAlternativeSuggestionData(ingredientResponse),
    date: new SemboDate(ingredientResponse.date).format(DateFormat.DateMonth),
    isAvailable: false,
  };
}

function getAlternativeSuggestionData(
  ingredientResponse: AlternativeActivityIngredientResponseWithStaticInfo
): AlternativeActivitySuggestionData {
  return {
    tickets: ingredientResponse.alternativeSuggestion.tickets.map((ticket) => ({
      quantity: ticket.quantity,
      ageRangeId: ticket.ageRangeId,
      rateId: ticket.rateId,
      timeSlot: ticket.timeSlotId,
      supplierToken: ticket.supplierToken,
    })),
  };
}

export function getTripOverviewUnavailableActivitySegment(
  parentIngredientId: IngredientId,
  ingredientId: IngredientId,
  activityDate: Date,
  ingredientIndex: number,
  selectedDate?: Date
): ItineraryActivitySegment {
  return {
    segmentType: ItinerarySegmentType.Activity,
    statusType: ItineraryActivitySegmentStatus.NoAvailable,
    isAvailable: false,
    productCardData: null,
    context: {
      parentIngredientId,
      ingredientId,
      ingredientIndex,
    },
    date: new SemboDate(activityDate).format(DateFormat.DateMonth),
    reasons: getUnavailableActivitySegmentReasons(selectedDate),
  };
}

export function getUnavailableActivitySegmentReasons(
  selectedDate?: Date
): UnavailabilityReason[] {
  const reasons: UnavailabilityReason[] = [];

  if (selectedDate) {
    reasons.push(UnavailabilityReason.SelectedDate);
  }

  reasons.push(UnavailabilityReason.SelectedProduct);

  return reasons;
}

export function getUnavailableActivityMessage(
  reasons: UnavailabilityReason[]
): string {
  if (isEqual(reasons, [UnavailabilityReason.SelectedProduct])) {
    return t('planner.no_availability.messages.activity_selected');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedProduct)
  ) {
    return t(
      'planner.no_availability.messages.activity_selected_date_selected'
    );
  }

  return t('notification.no_available_with_current_filters');
}

export function getUppsellSegment(
  ingredient: HotelIngredient | OwnStayIngredient,
  areThereActivitiesInDestination: boolean,
  activitySuggestions: ActivitySuggestionWithStaticInfo[],
  staticActivitySuggestions: ActivityStaticInfo[],
  moneyFormanterFn: (amount: number, currency: string) => string
): ItineraryUpsellSegment | null {
  const hasIngredientActivity = ingredient.subIngredients.length > 0;

  const suggestedActivitiesBanner: SuggestedActivitiesBannerData = {
    isVisible: activitySuggestions.length > 0 && !hasIngredientActivity,
    suggestedActivities: activitySuggestions.map(
      ({ images, name, price, code }) => ({
        price: price
          ? moneyFormanterFn(price.amount, price.currency)
          : undefined,
        name,
        imageUrl: getImageUrl(images[0]),
        productCode: code,
      })
    ),
    destination: ingredient.displayText,
  };

  const staticSuggestedActivitiesBanner: StaticSuggestedActivitiesBannerData = {
    isVisible: staticActivitySuggestions.length > 0 && !hasIngredientActivity,
    suggestedActivities: staticActivitySuggestions.map(
      ({ hotelCode, images, name }) => ({
        imageUrl: getImageUrl(images[0]),
        name,
        productCode: hotelCode,
      })
    ),
    destination: ingredient.displayText.split(',')[0] ?? ingredient.displayText,
  };

  const genericActivityBanner: GenericActivityBannerData = {
    isVisible: hasIngredientActivity && areThereActivitiesInDestination,
  };

  if (
    !genericActivityBanner.isVisible &&
    !suggestedActivitiesBanner.isVisible &&
    !staticSuggestedActivitiesBanner.isVisible
  ) {
    return null;
  }

  return {
    segmentType: ItinerarySegmentType.Upsell,
    productCardData: {
      genericActivityBanner,
      suggestedActivitiesBanner,
      staticSuggestedActivitiesBanner,
      ingredientId: ingredient.ingredientId,
    },
  };
}
